import { create } from 'zustand'
import { combine, devtools } from 'zustand/middleware'

const OPTIMISTIC_DRAFT_TTL_MS = 60_000

/* Track per-thread timeout IDs outside of zustand state to avoid
 * re-renders on timeout bookkeeping changes. */
const timeoutByThreadId = new Map<string, number>()

export const useOptimisticDraftStore = create(
  devtools(
    combine(
      {
        draftIdsByThreadId: new Map<string, number | string>(),
        draftingMedicationIds: new Set<string>(),
      },
      (set) => ({
        setDraft: (threadId: string, draftId: number | string) => {
          const existing = timeoutByThreadId.get(threadId)
          if (existing !== undefined) {
            clearTimeout(existing)
          }

          set((state) => {
            const next = new Map(state.draftIdsByThreadId)
            next.set(threadId, draftId)
            return { draftIdsByThreadId: next }
          })

          const timeoutId = window.setTimeout(() => {
            timeoutByThreadId.delete(threadId)
            set((state) => {
              if (!state.draftIdsByThreadId.has(threadId)) {
                return state
              }
              const next = new Map(state.draftIdsByThreadId)
              next.delete(threadId)
              return { draftIdsByThreadId: next }
            })
          }, OPTIMISTIC_DRAFT_TTL_MS)
          timeoutByThreadId.set(threadId, timeoutId)
        },
        clearDraft: (threadId: string) => {
          const existing = timeoutByThreadId.get(threadId)
          if (existing !== undefined) {
            clearTimeout(existing)
            timeoutByThreadId.delete(threadId)
          }

          set((state) => {
            if (!state.draftIdsByThreadId.has(threadId)) {
              return state
            }
            const next = new Map(state.draftIdsByThreadId)
            next.delete(threadId)
            return { draftIdsByThreadId: next }
          })
        },
        addDraftingMedication: (medicationId: string) => {
          set((state) => {
            const next = new Set(state.draftingMedicationIds)
            next.add(medicationId)
            return { draftingMedicationIds: next }
          })
        },
        removeDraftingMedication: (medicationId: string) => {
          set((state) => {
            const next = new Set(state.draftingMedicationIds)
            next.delete(medicationId)
            return { draftingMedicationIds: next }
          })
        },
      })
    ),
    { name: 'optimistic-drafts' }
  )
)
